import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelpCenter, NewVersionBanner } from '@dataroom/shared';
import { Layout, PortalFrame } from '@/components/Layout';
import { contractsEnabled } from '@/lib/features';
import { ContractsPage } from '@/pages/ContractsPage';
import { LoginPage } from '@/pages/LoginPage';
import { MyTeamPage } from '@/pages/MyTeamPage';
import { RoomPage } from '@/pages/RoomPage';
import { RoomsLandingPage } from '@/pages/RoomsLandingPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

// The hosted mock demo is served as static files (no server-side SPA
// fallback), so it uses hash routing to survive deep-link refreshes. The real
// app uses history-based routing behind the Pages/API origin.
const Router = import.meta.env.VITE_USE_MOCK === '1' ? HashRouter : BrowserRouter;

/** Offers a reload once a newer build is deployed (web/shared/src/ui/new-version-banner.tsx).
 * Off in dev and in the mock demo build, where there is no deploy to compare. */
function NewVersionNotice() {
  const { pathname } = useLocation();
  return (
    <NewVersionBanner
      currentVersion={import.meta.env.VITE_APP_VERSION}
      enabled={import.meta.env.PROD && import.meta.env.VITE_USE_MOCK !== '1'}
      routeKey={pathname}
    />
  );
}

/** The route table, exported so tests can mount it under a MemoryRouter. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<RoomsLandingPage />} />
      {/* The help center: static content, so it needs no room and works signed in or out. */}
      <Route
        path="/help/*"
        element={
          <PortalFrame>
            <HelpCenter audience="buyer" />
          </PortalFrame>
        }
      />
      <Route path="/rooms/:roomId" element={<Layout />}>
        <Route index element={<RoomPage />} />
        <Route path="team" element={<MyTeamPage />} />
        {/* Off on PROD (VITE_FEATURE_CONTRACTS=0): a deep link lands on the room home. */}
        <Route
          path="contracts"
          element={
            contractsEnabled() ? <ContractsPage /> : <Navigate to=".." relative="route" replace />
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppRoutes />
        <NewVersionNotice />
      </Router>
    </QueryClientProvider>
  );
}
